import { RefObject, useCallback, useEffect, useState } from 'react';
import { useKeyDown } from './useKeyDown';

const TABBABLE_SELECTOR =
  'a[href], area[href], button, iframe, input, select, summary, textarea, [contenteditable], [tabindex]';

const FOCUS_HISTORY_SIZE = 10;

const openDialogs: HTMLElement[] = [];

const openModalDialogs = new Set<HTMLElement>();

const focusHistory: Element[] = [];

const handledEscapeEvents = new WeakSet<KeyboardEvent>();

interface IsBeforeArgs {
  element: Node;
  target: Node;
}

interface ResolveOpenerArgs {
  dialog: HTMLElement;
  focusedBeforeOpen: Element | null;
}

interface UseDialogFocusArgs {
  dialogRef: RefObject<HTMLElement>;
  isOpen: boolean;
  onEscape: () => void;
  initialFocusRef?: RefObject<HTMLElement>;
  isModal?: boolean;
}

const rememberFocus = ({ target }: FocusEvent) => {
  if (!(target instanceof Element)) {
    return;
  }

  focusHistory.push(target);

  if (focusHistory.length > FOCUS_HISTORY_SIZE) {
    focusHistory.shift();
  }
};

const resolveOpener = ({ dialog, focusedBeforeOpen }: ResolveOpenerArgs) => {
  const { activeElement } = document;

  if (activeElement && activeElement !== document.body && !dialog.contains(activeElement)) {
    return activeElement;
  }

  if (!focusedBeforeOpen || focusedBeforeOpen.isConnected) {
    return focusedBeforeOpen;
  }

  return (
    [...focusHistory]
      .reverse()
      .find((element) => element.isConnected && !dialog.contains(element)) ?? null
  );
};

const isCoveredByAnotherLayer = (element: HTMLElement) => {
  const { left, top, width, height } = element.getBoundingClientRect();

  return !element.contains(document.elementFromPoint(left + width / 2, top + height / 2));
};

const isTopDialog = (dialog: HTMLElement) => openDialogs[openDialogs.length - 1] === dialog;

const updateInert = () => {
  const modalDialogs = openDialogs.filter((dialog) => openModalDialogs.has(dialog));
  const topModalDialog = modalDialogs[modalDialogs.length - 1];

  document.getElementById('root')?.toggleAttribute('inert', Boolean(topModalDialog));
  modalDialogs.forEach((dialog) => dialog.toggleAttribute('inert', dialog !== topModalDialog));
};

const isBefore = ({ element, target }: IsBeforeArgs) =>
  Boolean(target.compareDocumentPosition(element) & Node.DOCUMENT_POSITION_PRECEDING);

const isTabbable = (element: HTMLElement) =>
  element.tabIndex >= 0 &&
  !element.matches(':disabled') &&
  element.getClientRects().length > 0 &&
  getComputedStyle(element).visibility !== 'hidden';

document.addEventListener('focusin', rememberFocus);

export const useDialogFocus = ({
  dialogRef,
  isOpen,
  onEscape,
  initialFocusRef,
  isModal,
}: UseDialogFocusArgs) => {
  const [focusedBeforeOpen, setFocusedBeforeOpen] = useState(() =>
    isOpen ? document.activeElement : null
  );

  const [isOpenPrev, setIsOpenPrev] = useState(isOpen);

  if (isOpen !== isOpenPrev) {
    setIsOpenPrev(isOpen);
    setFocusedBeforeOpen(isOpen ? document.activeElement : null);
  }

  const escapeHandler = useCallback(
    (event: KeyboardEvent) => {
      if (
        handledEscapeEvents.has(event) ||
        !isOpen ||
        !dialogRef.current ||
        !isTopDialog(dialogRef.current)
      ) {
        return;
      }

      handledEscapeEvents.add(event);
      onEscape();
    },
    [dialogRef, isOpen, onEscape]
  );

  useKeyDown({ key: 'Escape', action: escapeHandler });

  useEffect(() => {
    const dialog = dialogRef.current;

    if (!isOpen || !dialog) {
      return;
    }

    const opener = resolveOpener({ dialog, focusedBeforeOpen });

    openDialogs.push(dialog);

    if (!dialog.contains(document.activeElement) && !isCoveredByAnotherLayer(dialog)) {
      (initialFocusRef?.current ?? dialog).focus({ preventScroll: true });
    }

    if (isModal) {
      openModalDialogs.add(dialog);
      updateInert();
    }

    const trapTabHandler = (event: KeyboardEvent) => {
      const { activeElement: focusedElement } = document;

      if (
        !isModal ||
        event.key !== 'Tab' ||
        !isTopDialog(dialog) ||
        !focusedElement ||
        !dialog.contains(focusedElement)
      ) {
        return;
      }

      const tabbables = Array.from(dialog.querySelectorAll<HTMLElement>(TABBABLE_SELECTOR)).filter(
        isTabbable
      );

      if (!tabbables.length) {
        event.preventDefault();
        return;
      }

      const firstTabbable = tabbables[0];
      const lastTabbable = tabbables[tabbables.length - 1];

      if (
        event.shiftKey &&
        (focusedElement === firstTabbable ||
          isBefore({ element: focusedElement, target: firstTabbable }))
      ) {
        event.preventDefault();
        lastTabbable.focus();
        return;
      }

      if (!event.shiftKey && focusedElement === lastTabbable) {
        event.preventDefault();
        firstTabbable.focus();
      }
    };

    document.addEventListener('keydown', trapTabHandler);

    return () => {
      openDialogs.splice(openDialogs.indexOf(dialog), 1);
      openModalDialogs.delete(dialog);
      document.removeEventListener('keydown', trapTabHandler);
      dialog.removeAttribute('inert');
      updateInert();

      if (
        opener instanceof HTMLElement &&
        (document.activeElement === document.body || dialog.contains(document.activeElement))
      ) {
        opener.focus({ preventScroll: true });
      }
    };
  }, [dialogRef, focusedBeforeOpen, initialFocusRef, isModal, isOpen]);
};
